
import { useState } from "react";
import { X, ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Ingredient, Step } from "@/types/recipe";
import { cn } from "@/lib/utils";
import { KeyboardEvent } from "react";

interface CookingCardsProps {
  isOpen: boolean;
  onClose: () => void;
  steps: (Step | string)[];
  ingredients: Ingredient[];
  temperatureUnit: "C" | "F";
}

const CookingCards = ({ isOpen, onClose, steps, ingredients, temperatureUnit }: CookingCardsProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showIngredients, setShowIngredients] = useState(false);

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") {
      handleNext();
    } else if (event.key === "ArrowLeft") {
      handlePrevious();
    } else if (event.key === "Escape") {
      onClose();
    }
  };

  const getCurrentStepContent = () => {
    const step = steps[currentStep];
    if (typeof step === "string") {
      return <div className="text-3xl text-center">{step}</div>;
    }
    return (
      <div className="space-y-4 text-center">
        <div className="text-3xl">
          {step.instruction}
        </div>
        {(step.temperature || step.time) && (
          <div className="text-2xl text-gray-500">
            {step.temperature && `${step.temperature}°${temperatureUnit}`}
            {step.temperature && step.time && " - "}
            {step.time}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="flex items-center justify-between p-4 border-b">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowIngredients(!showIngredients)}
        >
          <ClipboardList className="w-6 h-6" />
        </Button>
        <span className="font-medium">
          Step {currentStep + 1} of {steps.length}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </Button>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <div 
          className={cn(
            "absolute inset-0 transition-transform duration-300 flex items-center justify-center p-8",
            showIngredients ? "translate-y-0" : "-translate-y-full"
          )}
        >
          <div className="w-full max-w-2xl space-y-4">
            <h2 className="text-2xl font-bold mb-4">Ingredients</h2>
            {ingredients.map((ingredient, index) => (
              <div 
                key={index}
                className="flex justify-between items-center text-xl"
              >
                <span>{ingredient.name}</span>
                <span className="text-gray-600">
                  {ingredient.amount} {ingredient.unit}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div 
          className={cn(
            "absolute inset-0 transition-transform duration-300 flex items-center justify-center p-8",
            showIngredients ? "translate-y-full" : "translate-y-0"
          )}
        >
          <div 
            className="max-w-4xl w-full"
            onClick={() => handleNext()}
          >
            {getCurrentStepContent()}
          </div>
        </div>
      </div>

      <div className="p-4 border-t space-y-4">
        <Progress value={progress} className="w-full" />
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="w-6 h-6" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleNext}
            disabled={currentStep === steps.length - 1}
          >
            Next
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookingCards;
